import * as React from "react";
import { ScheduleDateTimePicker } from "@/components/ScheduleDateTimePicker";
import { usePostFlowContext } from "@/components/PostDialogs/SharedPostFlowContext";
import { Button, Popover, SimpleSelect, Skeleton, Tooltip } from "@/components/ui";
import { socialIcons, socialNames, SocialNetworksId } from "@/interfaces";
import { format } from "date-fns";
import { CalendarClock } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  addYears,
  useSocialCoachSession,
  useSocialNetworks,
  useSocialProfiles,
  promptSpecificDate,
  validateMessageLengthInSocialNetworks,
} from "@/utils";

export type PostTiming = "now" | "later" | "phone";

const TIMING_ITEMS: { label: string; value: PostTiming }[] = [
  { label: "Now", value: "now" },
  { label: "Schedule for later", value: "later" },
  { label: "Send to my phone", value: "phone" },
];

/** A coach posting for a player sends it to the player's app instead of their own phone. */
function coachTimingItems(
  first: string,
  noNetworks: boolean
): { label: string; value: PostTiming; disabled?: boolean }[] {
  return [
    { label: "Now", value: "now", disabled: noNetworks },
    { label: "Schedule for later", value: "later", disabled: noNetworks },
    { label: `Send to ${first} to review and post`, value: "phone" },
  ];
}

export const firstNameOf = (name: string | null | undefined) => name?.trim().split(/\s+/)[0] || null;

export function postTimingOf(isAutoPost: boolean, sendMode: string): PostTiming {
  if (!isAutoPost) return "phone";
  return sendMode === "SCHEDULED" ? "later" : "now";
}

/**
 * Where and when the post goes, on one line for the dialog's footer. Every connected network starts
 * selected, so posting right away takes no choices at all; a scheduled time is picked in a popover so
 * the footer never grows.
 */
export const PostOptions: React.FC = () => {
  const { isPlayer } = useSocialCoachSession();
  const { deliveryOptions, setDeliveryOptions, message, onBehalfOf, effectivePlayerUserId, effectivePlayer } =
    usePostFlowContext();
  const { postableSocialNetworks } = useSocialNetworks();
  // The networks are the player's own, whether they post or a coach posts for them.
  const ownNetworks = onBehalfOf || isPlayer;
  const profiles = useSocialProfiles(ownNetworks ? effectivePlayerUserId : undefined);
  const { networksActiveForPosting } = profiles;
  // On behalf of a player their user id may still be loading, and until then nothing is known about their accounts.
  const isLoading = profiles.isLoading || (onBehalfOf && effectivePlayerUserId == null);
  const first = firstNameOf(effectivePlayer?.fullName) ?? "the player";

  const networks = React.useMemo(
    () =>
      ownNetworks ? postableSocialNetworks.filter(sn => networksActiveForPosting.includes(sn)) : postableSocialNetworks,
    [postableSocialNetworks, networksActiveForPosting, ownNetworks]
  );
  const noNetworks = onBehalfOf && !isLoading && networks.length === 0;

  React.useEffect(() => {
    if (networks.length > 0 && deliveryOptions.socialNetworks.length === 0) {
      setDeliveryOptions(options => ({ ...options, socialNetworks: networks }));
    }
  }, [networks]);

  // A player with no connected accounts can only get the post in their app.
  React.useEffect(() => {
    if (noNetworks && deliveryOptions.isAutoPost)
      setDeliveryOptions(options => ({ ...options, isAutoPost: false, sendMode: "IMMEDIATE" }));
  }, [noNetworks]);

  const [pickerOpen, setPickerOpen] = React.useState(false);
  const tooLongFor = validateMessageLengthInSocialNetworks(message);
  const tooLong = (sn: SocialNetworksId) =>
    tooLongFor.includes(socialNames[sn]) || (sn === "TW" && tooLongFor.includes("Twitter"));

  // A network the caption is too long for can't take the post, so it's taken out of the selection.
  const tooLongKey = networks.filter(tooLong).join(",");
  React.useEffect(() => {
    if (!tooLongKey) return;
    const blocked = tooLongKey.split(",");
    if (deliveryOptions.socialNetworks.some(sn => blocked.includes(sn)))
      setDeliveryOptions(options => ({
        ...options,
        socialNetworks: options.socialNetworks.filter(sn => !blocked.includes(sn)),
      }));
  }, [tooLongKey, deliveryOptions.socialNetworks]);
  const timing = postTimingOf(deliveryOptions.isAutoPost, deliveryOptions.sendMode);

  const toggle = (sn: SocialNetworksId) => {
    const selected = deliveryOptions.socialNetworks.includes(sn);
    setDeliveryOptions({
      ...deliveryOptions,
      socialNetworks: selected
        ? deliveryOptions.socialNetworks.filter(n => n !== sn)
        : [...deliveryOptions.socialNetworks, sn],
    });
  };

  const setTiming = (next: PostTiming) => {
    setDeliveryOptions({
      ...deliveryOptions,
      isAutoPost: next !== "phone",
      sendMode: next === "later" ? "SCHEDULED" : "IMMEDIATE",
    });
    if (next === "later") setPickerOpen(true);
  };

  const scheduledAt =
    deliveryOptions.date && deliveryOptions.time
      ? promptSpecificDate(deliveryOptions.date, deliveryOptions.time)
      : null;

  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-2" data-testid="post-options">
      <div className="flex items-center gap-2">
        <span className="text-xs font-bold uppercase tracking-wider text-slate-400">Post to</span>
        {noNetworks ? (
          <span className="text-sm text-slate-500">{first} hasn't connected any social accounts.</span>
        ) : timing === "phone" ? (
          <span className="text-sm text-slate-500">{onBehalfOf ? `${first}'s phone` : "Your phone"}</span>
        ) : isLoading ? (
          <>
            <Skeleton className="size-7 rounded-full" />
            <Skeleton className="size-7 rounded-full" />
          </>
        ) : (
          networks.map(sn => {
            const selected = deliveryOptions.socialNetworks.includes(sn);
            const blocked = tooLong(sn);
            const button = (
              <button
                key={sn}
                type="button"
                aria-pressed={selected}
                aria-label={socialNames[sn]}
                disabled={blocked}
                onClick={() => toggle(sn)}
                className={cn(
                  "flex rounded-full p-0.5 ring-2 transition cursor-pointer disabled:cursor-not-allowed",
                  selected && !blocked ? "ring-primary" : "ring-transparent opacity-40 grayscale"
                )}
              >
                <img src={socialIcons[sn]} alt="" className="size-6 object-contain" />
              </button>
            );
            return blocked ? (
              <Tooltip key={sn}>
                <Tooltip.Trigger render={<span className="inline-flex" />}>{button}</Tooltip.Trigger>
                <Tooltip.Content>The caption is too long for {socialNames[sn]}, so it's left out.</Tooltip.Content>
              </Tooltip>
            ) : (
              button
            );
          })
        )}
      </div>
      <div className="flex items-center gap-2">
        <SimpleSelect
          items={onBehalfOf ? coachTimingItems(first, noNetworks) : TIMING_ITEMS}
          value={timing}
          onValueChange={value => setTiming((value as PostTiming) ?? "now")}
          className="text-sm font-semibold text-slate-700"
          align="start"
        />
        {timing === "later" && (
          <Popover open={pickerOpen} onOpenChange={setPickerOpen}>
            <Popover.Trigger
              render={
                <Button variant="outline" size="sm">
                  <CalendarClock />
                  {scheduledAt ? format(new Date(scheduledAt), "MMM d, h:mm a") : "Pick a time"}
                </Button>
              }
            />
            <Popover.Content className="w-auto p-3" side="top">
              <ScheduleDateTimePicker
                value={scheduledAt ? new Date(scheduledAt) : undefined}
                onChange={value => setDeliveryOptions({ ...deliveryOptions, date: value, time: value })}
                maxDate={addYears(new Date(), 1)}
              />
            </Popover.Content>
          </Popover>
        )}
      </div>
    </div>
  );
};
